import { getDecryptedLocalStorageItem } from './encryptData';

/*
 * environmentScope.ts
 * Keeps the environment of a browser tab in the session storage, which
 * is private per tab and survives reloads. A superuser or staff user who
 * switches environment in one tab keeps every other tab in the environment
 * it was opened in: each request carries the hash of the tab as header and
 * the backend scopes the request user to that environment in memory. The
 * local storage keeps the environment of the last switch as default for
 * new tabs.
 */

// Session storage keys of the tab scope
const TAB_ENVIRONMENT_KEY = 'tab_environment_hash';
const TAB_USER_KEY = 'tab_user_hash';

// Reads a session storage item, storage access can throw in private windows
const readSessionItem = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

// Returns the environment hash of the default session stored in the local storage
export const getDefaultEnvironmentHash = (): string | null => {
    try {
        return getDecryptedLocalStorageItem('environment_hash');
    } catch (error) {
        return null;
    }
};

// Returns the user hash of the default session stored in the local storage
const getDefaultUserHash = (): string | null => {
    try {
        return getDecryptedLocalStorageItem('user_hash');
    } catch (error) {
        return null;
    }
};

// Returns the environment hash this tab is pinned to, or null when the tab has no valid scope
export const getTabEnvironmentHash = (): string | null => {
    const tabEnvironmentHash = readSessionItem(TAB_ENVIRONMENT_KEY);
    const tabUserHash = readSessionItem(TAB_USER_KEY);

    // No scope without a pinned environment
    if (!tabEnvironmentHash || !tabUserHash) return null;

    // The scope belongs to a session which ended or to another user who logged in meanwhile
    let currentSession: string | null = null;
    try {
        currentSession = localStorage.getItem('currentSession');
    } catch (error) {
        return null;
    }
    if (!currentSession || getDefaultUserHash() !== tabUserHash) return null;

    return tabEnvironmentHash;
};

// Pins this tab to the given environment of the given user
export const setTabScope = (environmentHash: string, userHash: string) => {
    try {
        sessionStorage.setItem(TAB_ENVIRONMENT_KEY, environmentHash);
        sessionStorage.setItem(TAB_USER_KEY, userHash);
    } catch (error) {
        // Ignore storage errors, the tab then follows the default environment
    }
};

// Removes the scope of this tab
export const clearTabScope = () => {
    try {
        sessionStorage.removeItem(TAB_ENVIRONMENT_KEY);
        sessionStorage.removeItem(TAB_USER_KEY);
    } catch (error) {
        // Ignore storage errors
    }
};

// Returns whether this tab is in the default environment of the local storage
export const isTabOnDefaultEnvironment = (): boolean => {
    const tabEnvironmentHash = getTabEnvironmentHash();
    return tabEnvironmentHash === null || tabEnvironmentHash === getDefaultEnvironmentHash();
};
